import { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, createBrowserRouter, createRoutesFromElements } from "react-router-dom";

import { App } from "../../App";
import { ApisPage } from "../../apis/components";
import { ApiPage } from "../../apis/components/api-page";
import { CreateApiFormikWrapper } from "../../apis/components/create-api-page/form";
import { ApisContextProvider } from "../../apis/context";
import { AssetSubmission, AssetsContainer, AssetsList } from "../../assets/components";
import { AppDetailsContainer } from "../../camkit/apps/app-details";
import {
    AppListContainer,
    DashboardContainer as AppsDashboardContainer,
    NewAppContainer,
} from "../../camkit/apps/containers";
import { CamkitOutletWithRedirect } from "../../camkit/common/components";
import { DashboardContainer, OrganizationContainer } from "../../camkit/dashboard/containers";
import { EulaContainer } from "../../camkit/eula/components/EulaContainer";
import { GroupDetailsContainerV2 } from "../../camkit/groups/group-details/containers";
import { GlobalLensDetailsContainerV2 } from "../../camkit/lenses/lens-details/containers";
import { CreateOrganizationAndLensFolderWrapper } from "../../create-org-lens-folder/components";
import { EmbedDefaultOrgRedirect } from "../../embed/components/EmbedDefaultOrgRedirect";
import { ErrorBoundary, ProjectId } from "../../error-boundary";
import { CofConfigId } from "../../gql-generated/types";
import { HomePage } from "../../home/components/HomePage";
import { HostedLensesPage } from "../../hosted-lenses/components/HostedLensesPage";
import { UnauthorizedPage } from "../../layout/components";
import { LensPage } from "../../lens/components";
import { CommunityAudiencePage } from "../../lens-creator-insights/components";
import { LensStudioRedirectPage } from "../../lens-studio/components";
import { LensFoldersPage, LensesPageV3 } from "../../lenses/components";
import { MonetizationPage } from "../../monetization/components";
import { DEFAULT_MONETIZATION_TAB } from "../../monetization/models";
import { RemoteAssetDetailPage } from "../../remote-assets/components/RemoteAssetDetailPage";
import { RemoteAssetsPage } from "../../remote-assets/components/RemoteAssetsPage";
import { RedirectContainer } from "../../shared/components/RedirectContainer";
import { Feature } from "../../shared/models";
import { LensSubmission } from "../../submit/components";
import { LensSpaceLensDetails } from "../../submit/components/lens-details/lens-space";
import { LensSpaceLensesForUpdateContainer } from "../../submit/components/lens-for-update/lens-space";
import { CreateLensSpacePageContainer, SelectLensFolderPage } from "../../submit/components/lens-space";
import { LensSubmissionFlowRedirector } from "../../submit/components/lens-submission-flow-redirector";
import { NoRoleAssignedPage } from "../../submit/components/no-role-assigned";
import { SelectSubmitAccountPage } from "../../submit/components/select-submit-account";
import { SuccessfulSubmitPageContainer } from "../../submit/components/successful-submit-page";
import {
    WebLensPushToDevicePage,
    WebLensStartPage,
    WebLensSubmitPage,
    WebLensUpdatePage,
} from "../../web-lens/components";
import { Welcome } from "../components";

import { GatedRoute } from "./GatedRoute";
import { InsightsTabRoute } from "./InsightsTabRoute";
import { NavigateWithSearch } from "./NavigateWithSearch";
import { SentryRoute } from "./SentryRoute";
import { AbsoluteRoute } from "./absoluteRoutes";
import { RouteEntities, relativeRoutes } from "./relativeRoutes";

const EmbedApp = lazy(() => import("../../embed/EmbedApp"));

const {
    submit,
    createOrgLensFolder,
    lensStudioRedirect,
    apis,
    welcome,
    communityAudience,
    organization,
    lenses,
    lens,
    insights,
    monetization,
    accessDenied,
    noRoleAssigned,
    cameraKit,
    lensFolders,
    home,
    assets,
    hostedLenses,
} = relativeRoutes;

export const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route
                path="/embed/remote-assets"
                element={
                    <Suspense fallback={null}>
                        <EmbedApp />
                    </Suspense>
                }
            >
                <SentryRoute index element={<EmbedDefaultOrgRedirect />} />
                <SentryRoute path="*" element={<EmbedDefaultOrgRedirect />} />
            </Route>
            <Route
                path="/embed/:orgId"
                element={
                    <Suspense fallback={null}>
                        <EmbedApp />
                    </Suspense>
                }
            >
                <SentryRoute path="remote-assets" element={<GatedRoute gateKey={CofConfigId.MyLensesRemoteAssets} />}>
                    <SentryRoute index element={<RemoteAssetsPage />} />
                    <SentryRoute path=":remoteAssetId" element={<RemoteAssetDetailPage />} />
                </SentryRoute>
            </Route>
            <Route path={AbsoluteRoute.ROOT} element={<App />}>
                <SentryRoute path={accessDenied} element={<UnauthorizedPage />} />
                <SentryRoute path={noRoleAssigned} element={<NoRoleAssignedPage />} />

                {/* WELCOME ROUTES */}
                <SentryRoute path={welcome.index} element={<Welcome />}>
                    <SentryRoute index element={<Welcome />} />
                    <SentryRoute path={welcome.organization} element={<Welcome />} />
                </SentryRoute>
                {/* END WELCOME ROUTES */}

                <SentryRoute path={lensStudioRedirect} element={<LensStudioRedirectPage />} />

                <SentryRoute path={createOrgLensFolder} element={<CreateOrganizationAndLensFolderWrapper />} />

                <SentryRoute
                    path={hostedLenses}
                    element={
                        <RedirectContainer feature={Feature.hostedLenses}>
                            <HostedLensesPage />
                        </RedirectContainer>
                    }
                />

                {/* CAMERA KIT CORE ROUTES */}
                <SentryRoute
                    path={cameraKit.index}
                    element={
                        <GatedRoute gateKey={CofConfigId.MyLensesPortalUnification}>
                            <CamkitOutletWithRedirect />
                        </GatedRoute>
                    }
                >
                    <SentryRoute index element={<Navigate to={cameraKit.lensScheduler} />} />

                    <SentryRoute path={cameraKit.eula} element={<EulaContainer />} />

                    {/* Need organization redirects */}
                    <SentryRoute path={cameraKit.lensScheduler} element={<CamkitOutletWithRedirect />}>
                        <SentryRoute index element={<OrganizationContainer />} />
                    </SentryRoute>
                    <SentryRoute path={cameraKit.apps} element={<CamkitOutletWithRedirect />}>
                        <SentryRoute index element={<AppsDashboardContainer />} />
                    </SentryRoute>

                    {/* If no matches, redirect to apps page */}
                    <SentryRoute path="*" element={<Navigate to={cameraKit.apps} />} />
                </SentryRoute>
                {/* END CAMERA KIT CORE ROUTES  */}

                <SentryRoute path={assets.index} element={<GatedRoute gateKey={CofConfigId.MyLensesUiAssetLibrary} />}>
                    <SentryRoute index element={<AssetsList />} />
                    <SentryRoute path={assets.create} element={<AssetSubmission />} />
                </SentryRoute>

                {/* SUBMIT ROUTES  */}
                <SentryRoute path={submit.index} element={<Outlet />}>
                    <SentryRoute index element={<LensSubmission />} />
                    <SentryRoute path={submit.flowRedirector} element={<LensSubmissionFlowRedirector />} />

                    <SentryRoute path={submit.lenses.index} element={<Outlet />}>
                        <SentryRoute path={submit.lenses.create} element={<LensSpaceLensDetails />} />
                        <SentryRoute path={submit.lenses.update} element={<LensSpaceLensDetails />} />
                        <SentryRoute path={submit.lenses.select} element={<LensSpaceLensesForUpdateContainer />} />
                        <SentryRoute path={submit.lenses.success} element={<SuccessfulSubmitPageContainer />} />
                    </SentryRoute>

                    <SentryRoute path={submit.lensFolders.index} element={<Outlet />}>
                        <SentryRoute path={submit.lensFolders.create} element={<CreateLensSpacePageContainer />} />
                        <SentryRoute path={submit.lensFolders.select} element={<SelectLensFolderPage />} />
                    </SentryRoute>

                    <SentryRoute path={submit.accountsSelect} element={<SelectSubmitAccountPage />} />
                </SentryRoute>
                {/* END SUBMIT ROUTES */}

                {/* APIS ROUTES */}
                <SentryRoute
                    path={apis.index}
                    element={
                        <ErrorBoundary projectId={ProjectId.WEB_APIS}>
                            <ApisContextProvider>
                                <Outlet />
                            </ApisContextProvider>
                        </ErrorBoundary>
                    }
                >
                    <SentryRoute index element={<ApisPage />} />
                    <SentryRoute path={apis.api} element={<ApiPage />} />
                    <SentryRoute path={apis.create} element={<CreateApiFormikWrapper />} />
                </SentryRoute>
                {/* END APIS ROUTES */}

                {/* TODO: Needs to be a differente route */}
                {/* <SentryRoute path={createOrgLensFolder}  element={<GatedRoute gateKey={CofConfigId.MyLensesUxImprovements} />} >
          <SentryRoute index element={<LensFoldersPage />} />
      </SentryRoute> */}

                <SentryRoute
                    path={communityAudience}
                    element={<GatedRoute gateKey={CofConfigId.MyLensesUiCommunityAudience} />}
                >
                    <SentryRoute index element={<CommunityAudiencePage />} />
                </SentryRoute>

                <SentryRoute path={insights.index} element={<Outlet />}>
                    <SentryRoute index element={<NavigateWithSearch to={RouteEntities.Audience} replace />} />
                    <SentryRoute path={insights.insightsTab} element={<InsightsTabRoute />} />
                </SentryRoute>
                <SentryRoute element={<GatedRoute gateKey={CofConfigId.MyLensesMonetizationInsights} />}>
                    <SentryRoute path={monetization.index} element={<Outlet />}>
                        <SentryRoute index element={<NavigateWithSearch to={DEFAULT_MONETIZATION_TAB} replace />} />
                        <SentryRoute path={monetization.monetizationTab} element={<MonetizationPage />} />
                    </SentryRoute>
                </SentryRoute>
                <SentryRoute path={lenses} element={<LensesPageV3 />} />
                <SentryRoute path={lens} element={<LensPage />} />

                <SentryRoute
                    path={lensFolders}
                    element={
                        <GatedRoute gateKey={CofConfigId.MyLensesUxImprovements} fallbackPath={AbsoluteRoute.LENSES} />
                    }
                >
                    <SentryRoute index element={<LensFoldersPage />} />
                </SentryRoute>

                <SentryRoute path={home} element={<Outlet />}>
                    <SentryRoute index element={<HomePage />} />
                </SentryRoute>

                {/* ORGANIZATION ROUTES */}
                <SentryRoute path={organization.index} element={<Outlet />}>
                    <SentryRoute index element={<Navigate to={RouteEntities.LensFolders} />} />

                    <SentryRoute path={organization.insights.index} element={<Outlet />}>
                        <SentryRoute index element={<NavigateWithSearch to={RouteEntities.Audience} replace />} />
                        <SentryRoute path={organization.insights.insightsTab} element={<InsightsTabRoute />} />
                    </SentryRoute>
                    <SentryRoute element={<GatedRoute gateKey={CofConfigId.MyLensesMonetizationInsights} />}>
                        <SentryRoute path={organization.monetization.index} element={<Outlet />}>
                            <SentryRoute index element={<NavigateWithSearch to={DEFAULT_MONETIZATION_TAB} replace />} />
                            <SentryRoute
                                path={organization.monetization.monetizationTab}
                                element={<MonetizationPage />}
                            />
                        </SentryRoute>
                    </SentryRoute>
                    <SentryRoute path={organization.lenses} element={<LensesPageV3 />} />
                    <SentryRoute element={<GatedRoute gateKey={CofConfigId.MyLensesUiAssetLibrary} />}>
                        <SentryRoute path={organization.assets.index} element={<AssetsContainer />}>
                            <SentryRoute index element={<AssetsList />} />
                            <SentryRoute path={organization.assets.create} element={<AssetSubmission />} />
                        </SentryRoute>
                    </SentryRoute>

                    {/* WEB LENS — COF-gated creator allowlist (server-side enforced in the BFF
                        too). Org-scoped like every container feature: the path owns the org, the
                        ?lens-folder= query param carries the Lens Space between the two steps
                        (pick a folder, then upload). */}
                    <SentryRoute
                        path={organization.webLenses.index}
                        element={
                            <GatedRoute gateKey={CofConfigId.MyLensesWebLensPublication}>
                                <Outlet />
                            </GatedRoute>
                        }
                    >
                        <SentryRoute index element={<WebLensStartPage />} />
                        <SentryRoute path={organization.webLenses.submit} element={<WebLensSubmitPage />} />
                        <SentryRoute
                            path={`${organization.webLenses.update}/:lensId`}
                            element={<WebLensUpdatePage />}
                        />
                        <SentryRoute path={organization.webLenses.pushToDevice} element={<WebLensPushToDevicePage />} />
                    </SentryRoute>

                    <SentryRoute path={organization.lensFolders.index} element={<Outlet />}>
                        <SentryRoute
                            element={
                                <GatedRoute
                                    gateKey={CofConfigId.MyLensesUxImprovements}
                                    fallbackPath={`../${RouteEntities.Lenses}`}
                                />
                            }
                        >
                            <SentryRoute index element={<LensFoldersPage />} />
                        </SentryRoute>
                        <SentryRoute
                            path={organization.lensFolders.lensFolder}
                            element={<Navigate to={RouteEntities.Lenses} />}
                        />
                        {/* <ProtectedSentryRoute
                        index
                        gateKey={CofConfigId.MyLensesUxImprovements}
                        element={null}
                        fallback={
                        <SentryRoute
                                path={organization.lensFolders.lensFolder}
                                element={<Navigate to={RouteEntities.Lenses} />}
                            />
                        }
                    /> */}

                        <SentryRoute path={organization.lensFolders.insights.index} element={<Outlet />}>
                            <SentryRoute index element={<NavigateWithSearch to={RouteEntities.Audience} replace />} />
                            <SentryRoute
                                path={organization.lensFolders.insights.insightsTab}
                                element={<InsightsTabRoute />}
                            />
                        </SentryRoute>
                        <SentryRoute element={<GatedRoute gateKey={CofConfigId.MyLensesMonetizationInsights} />}>
                            <SentryRoute path={organization.lensFolders.monetization.index} element={<Outlet />}>
                                <SentryRoute
                                    index
                                    element={<NavigateWithSearch to={DEFAULT_MONETIZATION_TAB} replace />}
                                />
                                <SentryRoute
                                    path={organization.lensFolders.monetization.monetizationTab}
                                    element={<MonetizationPage />}
                                />
                            </SentryRoute>
                        </SentryRoute>
                        <SentryRoute path={organization.lensFolders.lenses} element={<LensesPageV3 />} />
                        <SentryRoute path={organization.lensFolders.lens} element={<LensPage />} />
                    </SentryRoute>

                    {/* CAMERA KIT */}
                    <SentryRoute
                        path={organization.cameraKit.index}
                        element={
                            <ErrorBoundary hideTopNav projectId={ProjectId.MY_LENSES}>
                                <GatedRoute gateKey={CofConfigId.MyLensesPortalUnification}>
                                    <CamkitOutletWithRedirect />
                                </GatedRoute>
                            </ErrorBoundary>
                        }
                    >
                        <SentryRoute
                            path={organization.cameraKit.lensScheduler.index}
                            element={<OrganizationContainer />}
                        >
                            {/* Groups */}
                            <SentryRoute path={organization.cameraKit.lensScheduler.groups.index}>
                                <SentryRoute index element={<DashboardContainer />} />
                                <SentryRoute
                                    path={organization.cameraKit.lensScheduler.groups.group}
                                    element={<GroupDetailsContainerV2 />}
                                />
                                <SentryRoute
                                    path={organization.cameraKit.lensScheduler.groups.lens}
                                    element={<GlobalLensDetailsContainerV2 />}
                                />
                            </SentryRoute>

                            {/* Lenses */}
                            <SentryRoute path={organization.cameraKit.lensScheduler.lenses.index}>
                                <SentryRoute index element={<DashboardContainer />} />
                                <SentryRoute
                                    path={organization.cameraKit.lensScheduler.lenses.lens}
                                    element={<GlobalLensDetailsContainerV2 />}
                                />
                            </SentryRoute>

                            {/* Sources */}
                            <SentryRoute
                                path={organization.cameraKit.lensScheduler.sources}
                                element={<DashboardContainer />}
                            />
                        </SentryRoute>

                        <SentryRoute path={organization.cameraKit.apps.index} element={<AppsDashboardContainer />}>
                            <SentryRoute index element={<AppListContainer />} />

                            <SentryRoute path={organization.cameraKit.apps.create} element={<NewAppContainer />} />
                            <SentryRoute path={organization.cameraKit.apps.app} element={<AppDetailsContainer />} />
                        </SentryRoute>
                    </SentryRoute>

                    <SentryRoute
                        path={organization.hostedLenses}
                        element={<GatedRoute gateKey={CofConfigId.MyLensesHostedLenses} />}
                    >
                        <SentryRoute index element={<HostedLensesPage />} />
                    </SentryRoute>
                    <SentryRoute
                        path={organization.remoteAssets}
                        element={<GatedRoute gateKey={CofConfigId.MyLensesRemoteAssets} />}
                    >
                        <SentryRoute index element={<RemoteAssetsPage />} />
                        <SentryRoute path=":remoteAssetId" element={<RemoteAssetDetailPage />} />
                    </SentryRoute>
                </SentryRoute>
                {/* ORGANIZATION ROUTES END  */}

                <SentryRoute path="*" element={<Navigate to={AbsoluteRoute.ROOT} />} />
            </Route>
        </>,
    ),
);
